import { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { useController } from 'react-hook-form'

const onlyDigits = text => text.replace(/\D/g, '')

// One-time code entered in separate boxes. The form value is the plain string of digits typed so far.
const CodeField = ({
  invalid = false,
  label,
  length = 6,
  name,
}) => {
  const id = `id-${name}`
  const { field: { value, onChange, onBlur } } = useController({ name })
  const inputs = useRef([])

  const focusBox = useCallback(index => {
    const box = inputs.current[Math.max(0, Math.min(index, length - 1))]
    if (box) box.focus()
  }, [length])

  const remove = useCallback(index => {
    onChange(value.slice(0, index) + value.slice(index + 1))
  }, [onChange, value])

  // Writes `digits` into the code from `position`. A pasted or autofilled code arrives whole in one box.
  const write = useCallback((position, digits) => {
    const start = Math.min(position, value.length)
    const next = (value.slice(0, start) + digits + value.slice(start + digits.length)).slice(0, length)
    onChange(next)
    focusBox(start + digits.length)
  }, [focusBox, length, onChange, value])

  const handleChange = index => ({ target }) => {
    // Delete, Cut and keyboards that do not send a Backspace key (some Android ones) end up here.
    if (!target.value) {
      if (value[index]) remove(index)
      return
    }

    let { value: typed } = target
    // Typing next to a digit that is already there: the box now holds two characters, and only the
    // one the user just typed (the one before the caret) is new.
    if (value[index] && typed.length === 2) typed = typed[(target.selectionStart || typed.length) - 1]

    const digits = onlyDigits(typed)
    if (digits) write(index, digits)
  }

  const handleKeyDown = index => event => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (value[index]) {
        remove(index)
      } else if (index > 0) {
        remove(index - 1)
        focusBox(index - 1)
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusBox(index - 1)
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusBox(index + 1)
    }
  }

  const handlePaste = event => {
    event.preventDefault()
    const digits = onlyDigits(event.clipboardData.getData('text'))
    if (digits) write(0, digits)
  }

  return (
    <div className="code-field" role="group" aria-labelledby={`${id}-label`}>
      <span id={`${id}-label`} className="code-field__label">{label}</span>
      <div className="code-field__digits">
        {Array.from({ length }, (_, index) => (
          <input
            key={index} // eslint-disable-line react/no-array-index-key
            ref={node => { inputs.current[index] = node }}
            className="code-field__digit"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            // The first box takes the whole SMS code when the browser or phone autofills it.
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoFocus={index === 0}
            aria-label={`${label} ${index + 1}`}
            aria-invalid={invalid ? true : undefined}
            data-testid={`code-digit-${index}`}
            value={value[index] || ''}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            onFocus={event => event.target.select()}
            onBlur={onBlur}
          />
        ))}
      </div>
    </div>
  )
}

CodeField.propTypes = {
  invalid: PropTypes.bool,
  label: PropTypes.string.isRequired,
  length: PropTypes.number,
  name: PropTypes.string.isRequired,
}

export default CodeField
